import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle, Check, Clock, Loader2, MessageCircle, Phone, Send, X, XCircle,
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import type { WhatsAppConversation, WhatsAppAppointmentRequest, WhatsAppMessage } from '../types/whatsapp';

const STATE_COLORS: Record<string, string> = {
  greeting: 'bg-blue-100 text-blue-800',
  collecting: 'bg-yellow-100 text-yellow-800',
  booking: 'bg-purple-100 text-purple-800',
  faq: 'bg-green-100 text-green-800',
  escalated: 'bg-red-100 text-red-800',
  closed: 'bg-gray-100 text-gray-600',
};

export function WhatsAppPage() {
  const { get, post } = useApi();

  const [tab, setTab] = useState<'conversations' | 'requests'>('conversations');
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [requests, setRequests] = useState<WhatsAppAppointmentRequest[]>([]);
  const [selectedConv, setSelectedConv] = useState<WhatsAppConversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Confirm modal
  const [showConfirm, setShowConfirm] = useState<string | null>(null);
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmTime, setConfirmTime] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchConversations = useCallback(async () => {
    const result = await get<WhatsAppConversation[]>('/whatsapp/conversations');
    if (result.success && result.data) setConversations(result.data);
  }, [get]);

  const fetchRequests = useCallback(async () => {
    const result = await get<WhatsAppAppointmentRequest[]>('/whatsapp/appointment-requests');
    if (result.success && result.data) setRequests(result.data);
  }, [get]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchConversations(), fetchRequests()]).finally(() => setLoading(false));
  }, [fetchConversations, fetchRequests]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConv?.messages]);

  const handleSelectConversation = useCallback(async (convId: string) => {
    const result = await get<WhatsAppConversation>(`/whatsapp/conversations/${convId}`);
    if (result.success && result.data) setSelectedConv(result.data);
  }, [get]);

  const handleReply = useCallback(async () => {
    if (!selectedConv || !replyText.trim()) return;
    setSending(true);
    const result = await post(`/whatsapp/conversations/${selectedConv.id}/reply`, { message: replyText });
    if (result.success) {
      setReplyText('');
      // Refresh conversation
      await handleSelectConversation(selectedConv.id);
    } else {
      setError(result.error || 'Failed to send');
    }
    setSending(false);
  }, [selectedConv, replyText, post, handleSelectConversation]);

  const handleClose = useCallback(async (convId: string) => {
    const result = await post(`/whatsapp/conversations/${convId}/close`, {});
    if (result.success) {
      setSelectedConv(null);
      fetchConversations();
    } else {
      setError(result.error || 'Failed to close conversation');
    }
  }, [post, fetchConversations]);

  const handleConfirmAppointment = useCallback(async (convId: string) => {
    if (!confirmDate || !confirmTime) return;
    const result = await post(`/whatsapp/appointment-requests/${convId}/confirm`, {
      date: confirmDate, startTime: confirmTime, durationMinutes: 30, appointmentType: 'consultation',
    });
    if (result.success) {
      setShowConfirm(null);
      setConfirmDate('');
      setConfirmTime('');
      fetchRequests();
      fetchConversations();
    } else {
      setError(result.error || 'Failed to confirm appointment');
    }
  }, [confirmDate, confirmTime, post, fetchRequests, fetchConversations]);

  const handleRejectAppointment = useCallback(async (convId: string) => {
    const result = await post(`/whatsapp/appointment-requests/${convId}/reject`, { reason: 'No availability' });
    if (result.success) {
      fetchRequests();
    } else {
      setError(result.error || 'Failed to reject appointment request');
    }
  }, [post, fetchRequests]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <main className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-green-600" /> WhatsApp
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => setTab('conversations')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${tab === 'conversations' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            Conversations
          </button>
          <button
            onClick={() => setTab('requests')}
            className={`px-4 py-2 text-sm font-medium rounded-md relative ${tab === 'requests' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            Booking Requests
            {requests.length > 0 && (
              <span className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                {requests.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {tab === 'conversations' && (
        <div className="flex gap-4 h-[calc(100vh-14rem)]">
          {/* Conversation list */}
          <div className="w-80 flex-shrink-0 bg-white rounded-lg shadow-sm border overflow-y-auto">
            {conversations.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">WhatsApp Business number pending activation. Conversations will appear here once connected.</p>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv.id)}
                  className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition-colors ${selectedConv?.id === conv.id ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {conv.patientName || conv.phoneNumber}
                    </span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${STATE_COLORS[conv.state] || STATE_COLORS.greeting}`}>
                      {conv.state}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{conv.lastMessage || 'No messages'}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {conv.messageCount || 0} messages
                    {conv.escalated && <span className="ml-1 text-red-500">ESCALATED</span>}
                  </p>
                </button>
              ))
            )}
          </div>

          {/* Chat view */}
          <div className="flex-1 flex flex-col bg-white rounded-lg shadow-sm border">
            {selectedConv ? (
              <>
                {/* Header */}
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      {selectedConv.patientName || selectedConv.phoneNumber}
                    </h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {selectedConv.phoneNumber}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedConv.escalated && (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Escalated
                      </span>
                    )}
                    {selectedConv.state !== 'closed' && (
                      <button onClick={() => handleClose(selectedConv.id)} className="text-xs text-gray-500 hover:text-red-600">
                        Close
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                  {selectedConv.messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${
                        msg.direction === 'outbound'
                          ? 'bg-green-100 text-gray-900'
                          : 'bg-white text-gray-900 border'
                      }`}>
                        <p>{msg.text}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                          {msg.sentBy ? ' (admin)' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply */}
                {selectedConv.state !== 'closed' && (
                  <div className="px-4 py-3 border-t flex gap-2">
                    <input
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()}
                      placeholder="Type a reply..."
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md"
                    />
                    <button
                      onClick={handleReply}
                      disabled={sending || !replyText.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <p>Select a conversation</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'requests' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {requests.length === 0 ? (
            <p className="p-8 text-center text-gray-500">No pending appointment requests.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Preferred</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {requests.map(req => (
                  <tr key={req.conversationId}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{req.patientName || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{req.phoneNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{req.reason || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {req.preferredDate || '—'} {req.preferredTime || ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setShowConfirm(req.conversationId); setConfirmDate(req.preferredDate || ''); setConfirmTime(req.preferredTime || '09:00'); }}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 flex items-center gap-1"
                        >
                          <Check className="h-3 w-3" /> Confirm
                        </button>
                        <button
                          onClick={() => handleRejectAppointment(req.conversationId)}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 flex items-center gap-1"
                        >
                          <XCircle className="h-3 w-3" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Confirm Appointment</h2>
              <button onClick={() => setShowConfirm(null)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={confirmDate} onChange={e => setConfirmDate(e.target.value)} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input type="time" value={confirmTime} onChange={e => setConfirmTime(e.target.value)} className="w-full px-3 py-2 border rounded" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowConfirm(null)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
              <button
                onClick={() => handleConfirmAppointment(showConfirm)}
                disabled={!confirmDate || !confirmTime}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
              >
                Confirm & Notify Patient
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
